import { Box, IconButton, Typography, Button, Stack } from '@mui/material';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { RiDeleteBin6Line } from 'react-icons/ri';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import Select from '@/components/Common/Select';

interface JuvonnoFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  setValue: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

const JuvonnoForm = ({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm,
  getValues,
  setValue
}: JuvonnoFormProps) => {
  const servicesField = configFields?.find((field: any) => field.key === 'services');
  const otherFields = configFields?.filter((field: any) => field.key !== 'services');

  const {
    fields: services,
    append,
    remove
  } = useFieldArray({
    control,
    name: `${namePrefix}.services`
  });

  useEffect(() => {
    const existing = getValues(`${namePrefix}.services`);
    if (!existing || existing.length === 0) {
      append({ service: '', appointment_type: [] });
    }
  }, [append, getValues, namePrefix]);

  return (
    <>
      {otherFields?.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}

      <Box mt={2}>
        <Typography fontSize={16} fontWeight={600} mb={2}>
          {servicesField?.label || 'Configuration'}
        </Typography>

        {services.map((service: any, index: number) => (
          <ServiceItem
            key={service.id}
            index={index}
            control={control}
            remove={remove}
            disabled={disabled}
            setValue={setValue}
            namePrefix={namePrefix}
            servicesField={servicesField}
            totalServices={services.length}
          />
        ))}

        <Box display='flex' justifyContent='center'>
          <Button variant='contained' disabled={disabled} onClick={() => append({ service: '', appointment_type: [] })}>
            + Add Another Service
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default JuvonnoForm;

const ServiceItem = ({ index, control, remove, disabled, setValue, namePrefix, totalServices, servicesField }: any) => {
  const rowPrefix = `${namePrefix}.services.${index}`;
  const serviceField = servicesField?.group?.find((field: any) => field.key === 'service');
  const appointmentTypeField = servicesField?.group?.find((field: any) => field.key === 'appointment_type');
  const serviceOptions = serviceField?.options || [];
  const appointmentTypeOptions = appointmentTypeField?.options || [];

  const allServices =
    useWatch({
      control,
      name: `${namePrefix}.services`
    }) || [];
  const selectedServiceId = allServices?.[index]?.service;
  const previousServiceId = useRef(selectedServiceId);

  useEffect(() => {
    if (previousServiceId.current !== selectedServiceId && previousServiceId.current !== undefined) {
      setValue(`${rowPrefix}.appointment_type`, []);
    }
    previousServiceId.current = selectedServiceId;
  }, [rowPrefix, selectedServiceId, setValue]);

  const selectedServiceValues = allServices
    .map((row: any, rowIndex: number) => (rowIndex === index ? null : row?.service))
    .filter(Boolean)
    .map((value: any) => String(value));
  const filteredServiceOptions = serviceOptions.filter((option: any) => {
    const optionValue = String(option?.value);
    return optionValue === String(selectedServiceId || '') || !selectedServiceValues.includes(optionValue);
  });

  const filteredAppointmentTypeOptions = appointmentTypeOptions.filter(
    (option: any) => String(option?.service_id) === String(selectedServiceId)
  );

  return (
    <Box p={3} mb={3} border='1px solid #E5E7EB' borderRadius='4px'>
      <Stack direction='row' justifyContent='space-between' mb={2}>
        <Typography fontWeight={600}>Service #{index + 1}</Typography>
        {totalServices > 1 && !disabled && (
          <IconButton color='error' onClick={() => remove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Stack>

      <Box display='flex' flexDirection='column' gap={2}>
        <Select
          label={serviceField?.label || 'Service'}
          name={`${rowPrefix}.service`}
          control={control}
          disabled={disabled}
          options={filteredServiceOptions}
          required={serviceField?.required}
          fullWidth
        />
        <Select
          label={appointmentTypeField?.label || 'Appointment Types'}
          name={`${rowPrefix}.appointment_type`}
          control={control}
          disabled={disabled || !selectedServiceId}
          options={filteredAppointmentTypeOptions}
          multiple
          required={appointmentTypeField?.required}
          fullWidth
        />
      </Box>
    </Box>
  );
};
